import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@material-ui/core';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { showToast } from '@klpkonsern/ui';

import { Tooltip, ActionButton } from '@klp/design';
import type { IContractToRegistrationProcessActivity } from '@klp/contractregistration/classes/ContractToRegistrationProcessResponse';
import { useExecuteKostnadsforvaltningTaskMutation } from '../services/rtkApi';
import { CustomFormField } from './FormField';
import type { FormField } from '../interfaces';
import {
    findCamundaPropertyValue,
    findCamundaValidation,
    formfieldIsHidden,
    withTextMaxLength,
} from '../utils';
import { AutoSaveFormfields } from './AutoSaveKontraktsforvaltningForm';
import type { Leverandor } from './LeverandorListSelector';
import { FormContextProvider } from '../context/FormContext';

export const createValidationSchema = (
    formFields: Array<FormField>,
    values: FormValuesType,
    t?: (key: string, options?: Record<string, unknown>) => string,
    // Autosave validates without required rules so drafts can still be saved
    options?: { skipRequired?: boolean },
) => {
    const schemaFields: { [fieldId: string]: Yup.AnySchema } = {};
    formFields.forEach((field) => {
        if (!field.id) return;

        const validation = field.validation ?? [];
        const properties = field.properties ?? [];

        const isReadOnly = Boolean(
            findCamundaValidation('readonly', validation),
        );
        const subtype = findCamundaPropertyValue('type', properties);
        const isHidden = formfieldIsHidden(field, values);

        if (isReadOnly || subtype === 'constant' || isHidden) {
            return;
        }

        const required =
            findCamundaValidation('required', validation) &&
            !options?.skipRequired;
        if (field.type === 'string') {
            let validator = Yup.string();

            if (required) {
                validator = validator.required('Required');

                if (field.id === 'vurderingsskjema') {
                    validator = validator.test(
                        'is-greater-than-zero',
                        t?.('project.errors.invalidTotalVurderingsskjema') ??
                            'Required',
                        (value) => {
                            if (value === undefined || value === '') {
                                return false;
                            }
                            const parsedValue = parseFloat(value);
                            return parsedValue > 0;
                        },
                    );
                }
            }

            if (field.id === 'prosjektNummerD365') {
                validator = validator.matches(
                    new RegExp('^([NDSE]{1}\\d{3}(\\.\\d{0,4})*)$'),
                    t?.('project.errors.invalidProjectNumber') ??
                        'Invalid project number',
                );
            }
            if (
                field.id === 'entreprenorListe' ||
                field.id === 'leverandorListe'
            ) {
                validator = validator.test(
                    'json',
                    t?.('project.errors.companyNotSelected') ??
                        'Company not selected',
                    (value) => {
                        try {
                            const virksomhetList = JSON.parse(
                                value!,
                            ) as Array<Leverandor>;

                            return virksomhetList.some(
                                (v) => v.organisasjonsnummer !== undefined,
                            );
                        } catch (error) {
                            return false;
                        }
                    },
                );
            }

            validator = withTextMaxLength(validator, subtype, t);

            schemaFields[field.id] = validator;
        }

        if (field.type === 'long') {
            let validator = Yup.number();

            if (required) {
                validator = validator.required('Required');
            }

            schemaFields[field.id] = validator;
        }

        if (field.type === 'date') {
            let validator = Yup.date();

            if (required) {
                validator = validator.required('Required');
            }

            schemaFields[field.id] = validator;
        }

        if (field.type === 'boolean') {
            let validator = Yup.boolean().nullable();

            if (required) {
                validator = validator.required('Required');
            }

            schemaFields[field.id] = validator;
        }

        if (field.type === 'enum') {
            let validator = Yup.string();

            if (required) {
                validator = validator.required('Required');
            }

            schemaFields[field.id] = validator;
        }
    });

    return Yup.object().shape(schemaFields);
};

export type FormValuesType = { [id: string]: string };

type FormContainerKontraktsforvaltningProps = {
    prosessInstansId: string;
    activity: IContractToRegistrationProcessActivity;
    isDisabled: boolean;
    completeButtonTranslation?: string;
    completeTaskCallback?: () => void;
    disableExplicitSave?: boolean;
};

export const FormContainerKontraktsforvaltning = ({
    prosessInstansId,
    activity,
    isDisabled,
    completeButtonTranslation,
    completeTaskCallback,
    disableExplicitSave = true,
}: FormContainerKontraktsforvaltningProps) => {
    const { t, i18n } = useTranslation();
    const [executeProsjektTask, { isLoading }] =
        useExecuteKostnadsforvaltningTaskMutation();
    const [formFields, setFormFields] = useState(activity.formFields ?? []);

    const autosaveValidationSchema = useMemo(
        () =>
            Yup.lazy((values: FormValuesType) =>
                createValidationSchema(formFields, values, t, {
                    skipRequired: true,
                }),
            ),
        [formFields, t],
    );

    if (!formFields && !activity.id) {
        return null;
    }

    const initialValues: FormValuesType = Object.fromEntries(
        formFields?.map((field) => [field.id, field.value ?? '']),
    );

    const handleSubmit = (values: FormValuesType) => {
        if (!activity.id) return;

        const updatedFormFields = formFields?.map((field) => {
            return { ...field, value: values[field.id] };
        });

        setFormFields(updatedFormFields);
        executeProsjektTask({
            oppgaveId: activity.id,
            formFields: updatedFormFields,
        })
            .unwrap()
            .then(() => {
                showToast({
                    body: t('prosjekt.successfulOperation'),
                    type: 'success',
                });
                if (completeTaskCallback) {
                    completeTaskCallback();
                }
            })

            .catch((e) => {
                const regex =
                    /Oppgave\/task Id \(([^)]+)\) is assigned to \(([^)]+)\) not to user \(([^)]+)\)/;

                const match = e?.data?.match(regex);
                showToast({
                    body: match
                        ? t('prosjekt.notAssignedToUser')
                        : t('general.error.message'),
                    type: 'error',
                });
            });
    };

    return (
        <FormContextProvider initialRegion={null}>
            <Formik
                name="formContainer"
                initialValues={initialValues}
                onSubmit={handleSubmit}
                validationSchema={() =>
                    Yup.lazy((values) =>
                        createValidationSchema(formFields, values, t),
                    )
                }
                validateOnChange={false}
                validateOnBlur={false}
            >
                {({ values }) => (
                    <Form noValidate={true}>
                        <AutoSaveFormfields
                            validationSchema={autosaveValidationSchema}
                            fields={formFields}
                            activity={activity}
                            isDisabled={isDisabled}
                        />
                        {formFields?.map((field, index) => {
                            return (
                                <CustomFormField
                                    containerModule="kontraktsforvaltning"
                                    prosessInstansId={prosessInstansId}
                                    key={`${field.id}-${index}`}
                                    field={field}
                                    fields={formFields}
                                    formIsDisabled={isDisabled}
                                    locale={i18n.language}
                                    hiddenField={formfieldIsHidden(
                                        field,
                                        values,
                                    )}
                                />
                            );
                        })}

                        {!disableExplicitSave && (
                            <Box
                                style={{
                                    cursor: 'not-allowed',
                                    width: 'max-content',
                                }}
                            >
                                <Tooltip
                                    title={t(
                                        'prosjekt.notAllowedToPerformAction',
                                    )}
                                    hideTooltip={!isDisabled}
                                >
                                    <ActionButton
                                        disabled={isDisabled || isLoading}
                                        type="submit"
                                        actionType="save"
                                        style={{
                                            display: 'flex',
                                            justifySelf: 'flex-end',
                                        }}
                                        translation={
                                            completeButtonTranslation ??
                                            'formGenerator.submit'
                                        }
                                    />
                                </Tooltip>
                            </Box>
                        )}
                    </Form>
                )}
            </Formik>
        </FormContextProvider>
    );
};
